import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronDown, faChevronUp, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import BacktestProgress from '../BacktestProgress/BacktestProgress';
import BacktestLogs from '../BacktestLogs';
import './BacktestStatusCard.css';
import { copyBacktestDebugInfo } from '../../utils/backtestDebugInfo';
import { normalizeBacktestResultStatus } from '../../utils/backtestHelpers';

// ============================================================================
// CHART DATA PERSISTENCE LAYER
// ============================================================================
// Uses localStorage for persistence across page refresh, with in-memory cache
// as fallback for Safari private mode and performance optimization.

const CHART_CACHE_PREFIX = 'backtest-chart-';
const CHART_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

// In-memory cache: Fast access + fallback when localStorage unavailable
const memoryCache = {};

/**
 * Check if localStorage is available (fails in Safari private mode)
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Get chart data from cache (localStorage + memory fallback)
 * @param {string} backtestId - The backtest ID
 * @returns {Array} Chart data array or empty array
 */
const getChartDataFromCache = (backtestId) => {
  if (!backtestId) return [];

  // Try memory cache first (fastest)
  if (memoryCache[backtestId]) {
    return memoryCache[backtestId];
  }

  // Try localStorage
  if (isLocalStorageAvailable()) {
    try {
      const cached = localStorage.getItem(`${CHART_CACHE_PREFIX}${backtestId}`);
      if (cached) {
        const { data, savedAt } = JSON.parse(cached);
        // Check if cache is still valid (not expired)
        if (Date.now() - savedAt < CHART_CACHE_MAX_AGE_MS && Array.isArray(data)) {
          // Also populate memory cache for faster subsequent access
          memoryCache[backtestId] = data;
          return data;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(`${CHART_CACHE_PREFIX}${backtestId}`);
        }
      }
    } catch (e) {
      console.warn('[BacktestStatusCard] Error reading from localStorage:', e);
    }
  }

  return [];
};

/**
 * Save chart data to cache (localStorage + memory)
 * @param {string} backtestId - The backtest ID
 * @param {Array} data - Chart data array
 */
const saveChartDataToCache = (backtestId, data) => {
  if (!backtestId || !Array.isArray(data)) return;

  // Always update memory cache (fast, reliable)
  memoryCache[backtestId] = data;

  // Try to persist to localStorage
  if (isLocalStorageAvailable()) {
    try {
      localStorage.setItem(`${CHART_CACHE_PREFIX}${backtestId}`, JSON.stringify({
        data,
        savedAt: Date.now()
      }));
    } catch (e) {
      // localStorage might be full or unavailable
      console.warn('[BacktestStatusCard] Error writing to localStorage:', e);
    }
  }
};

/**
 * Clean up old chart data entries from localStorage
 * Called periodically to prevent localStorage bloat
 */
const cleanupOldCacheEntries = () => {
  if (!isLocalStorageAvailable()) return;

  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CHART_CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const { savedAt } = JSON.parse(cached);
            if (Date.now() - savedAt > CHART_CACHE_MAX_AGE_MS) {
              keysToRemove.push(key);
            }
          }
        } catch (e) {
          // Invalid JSON, remove it
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('[BacktestStatusCard] Error cleaning up localStorage:', e);
  }
};

// Run cleanup on module load (once per session)
cleanupOldCacheEntries();

/**
 * BacktestStatusCard - Reusable card component for displaying running backtest status
 *
 * Features:
 * - Progress bar with percentage
 * - Portfolio value chart (grows as backtest progresses)
 * - Stop backtest button
 * - Expandable live logs section
 *
 * @param {Object} backtest - Backtest object with id, status, progress, etc.
 * @param {Function} onStop - Callback when stop button is clicked
 * @param {Function} getApiToken - Function to get API token for logs
 * @param {Array} chartData - Array of chart data points (optional, can build from progress)
 * @param {boolean} showLogs - Whether to show the logs toggle (default: true)
 * @param {string} className - Additional CSS classes
 * @param {string} startDate - Optional start date of the backtest period
 * @param {string} endDate - Optional end date of the backtest period
 */
export default function BacktestStatusCard({
  backtest,
  onStop,
  getApiToken,
  chartData: externalChartData,
  showLogs = true,
  className = '',
  startDate: propStartDate = null,
  endDate: propEndDate = null,
}) {
  const [isStopping, setIsStopping] = useState(false);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [debugCopySuccess, setDebugCopySuccess] = useState(false);

  // Compute stable backtest ID - use both id and backtestId to ensure consistency
  // The backend may return either or both depending on the endpoint
  const stableBacktestId = useMemo(() => {
    const id = backtest?.id || backtest?.backtestId || null;
    return id;
  }, [backtest?.id, backtest?.backtestId]);

  // Initialize chart data from cache on mount (localStorage + memory fallback)
  const [chartDataState, setChartDataState] = useState(() => {
    return getChartDataFromCache(stableBacktestId);
  });

  // Track last seen backtest ID to detect changes
  const lastBacktestIdRef = useRef(stableBacktestId);

  // When backtest ID changes, load existing chart data from cache or start fresh
  useEffect(() => {
    if (stableBacktestId && stableBacktestId !== lastBacktestIdRef.current) {
      // Load chart data for the new backtest from cache (localStorage + memory)
      const existingData = getChartDataFromCache(stableBacktestId);
      setChartDataState(existingData);
      lastBacktestIdRef.current = stableBacktestId;

    }
  }, [stableBacktestId]);

  // Accumulate chart data from progress updates
  useEffect(() => {
    // If external chart data is provided, use that instead
    if (externalChartData && externalChartData.length > 0) {
      return;
    }

    // Only accumulate if we have a valid backtest ID
    if (!stableBacktestId) {
      return;
    }

    const progress = backtest?.progress;
    if (progress && progress.portfolio_value && progress.simulation_date) {
      const portfolioValue = parseFloat(
        (progress.portfolio_value || '').toString().replace(/[^0-9.-]/g, '')
      );

      if (!isNaN(portfolioValue)) {
        setChartDataState(prevData => {
          // Check if this data point already exists (by date)
          const existingIndex = prevData.findIndex(
            p => p.date === progress.simulation_date
          );

          if (existingIndex === -1) {
            // Add new data point
            const newPoint = {
              x: prevData.length,
              y: portfolioValue,
              date: progress.simulation_date
            };
            const newData = [...prevData, newPoint];

            // CRITICAL: Save to cache (localStorage + memory) for persistence across refresh
            saveChartDataToCache(stableBacktestId, newData);

            return newData;
          } else {
            // Update existing point value (same date, new portfolio value)
            const updated = [...prevData];
            updated[existingIndex] = { ...updated[existingIndex], y: portfolioValue };
            saveChartDataToCache(stableBacktestId, updated);
            return updated;
          }
        });
      }
    }
  }, [backtest?.progress, externalChartData, stableBacktestId]);

  // Use external chart data if provided, otherwise use accumulated state
  const chartData = useMemo(() => {
    if (externalChartData && externalChartData.length > 0) {
      return externalChartData;
    }
    return chartDataState;
  }, [externalChartData, chartDataState]);

  // Elapsed time state with client-side timer that ticks up every second
  const [elapsedTime, setElapsedTime] = useState(0);
  const elapsedTimeRef = useRef(0);
  const timerIntervalRef = useRef(null);
  const timerStartedRef = useRef(false);

  // Determine if backtest is actively running - check multiple possible status values
  const isActivelyRunning = useMemo(() => {
    const status = normalizeBacktestResultStatus(backtest) || (backtest?.stage || '').toLowerCase();
    const runningStatuses = ['running', 'backtesting', 'in_progress', 'active', 'processing'];
    return runningStatuses.includes(status);
  }, [backtest]);

  // Helper function to parse elapsed time from backend data (not a hook, just a function)
  const getBackendElapsedSeconds = () => {
    if (backtest?.elapsed_ms) {
      return Math.floor(backtest.elapsed_ms / 1000);
    }
    if (backtest?.progress?.elapsed) {
      // Parse elapsed string like "0:27:56" to seconds
      const elapsed = backtest.progress.elapsed;
      if (typeof elapsed === 'string' && elapsed.includes(':')) {
        const parts = elapsed.split(':').map(Number);
        if (parts.length === 3) {
          return parts[0] * 3600 + parts[1] * 60 + parts[2];
        } else if (parts.length === 2) {
          return parts[0] * 60 + parts[1];
        }
      }
      // Could be a number in seconds
      const numElapsed = parseFloat(elapsed);
      if (!isNaN(numElapsed)) {
        return Math.floor(numElapsed);
      }
    }
    return null;
  };

  // Calculate initial elapsed from createdAt timestamp
  const getElapsedFromCreatedAt = () => {
    if (backtest?.createdAt) {
      const createdTime = new Date(backtest.createdAt).getTime();
      if (!isNaN(createdTime)) {
        return Math.max(0, Math.floor((Date.now() - createdTime) / 1000));
      }
    }
    return 0;
  };

  // Start/stop timer based on running state - only re-run when isActivelyRunning changes
  useEffect(() => {
    // Clear any existing timer first
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (isActivelyRunning) {
      // Initialize elapsed time if timer hasn't started yet
      if (!timerStartedRef.current) {
        let initialElapsed = getBackendElapsedSeconds();
        if (initialElapsed === null) {
          initialElapsed = getElapsedFromCreatedAt();
        }
        elapsedTimeRef.current = initialElapsed;
        setElapsedTime(initialElapsed);
        timerStartedRef.current = true;
      }

      // Start the timer - tick every second
      timerIntervalRef.current = setInterval(() => {
        elapsedTimeRef.current += 1;
        setElapsedTime(elapsedTimeRef.current);
      }, 1000);
    } else {
      // Reset the started flag when not running
      timerStartedRef.current = false;
    }

    // Cleanup on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isActivelyRunning]); // Only depend on isActivelyRunning - NOT on backend data

  // Sync with server elapsed time when backend data updates (without restarting timer)
  useEffect(() => {
    const backendElapsed = getBackendElapsedSeconds();
    if (backendElapsed !== null && backendElapsed > 0) {
      // Only sync if server time is significantly ahead (to avoid jitter)
      // Allow server to correct us if we're more than 2 seconds behind
      if (backendElapsed > elapsedTimeRef.current + 2) {
        elapsedTimeRef.current = backendElapsed;
        setElapsedTime(backendElapsed);
      }
    }
  }, [backtest?.elapsed_ms, backtest?.progress?.elapsed]);

  // Handle stop button click
  const handleStop = useCallback(async () => {
    if (!onStop || isStopping) return;

    setIsStopping(true);
    try {
      await onStop(backtest?.id);
    } catch (error) {
      console.error('[BacktestStatusCard] Error stopping backtest:', error);
    } finally {
      setIsStopping(false);
    }
  }, [onStop, backtest?.id, isStopping]);

  // Toggle logs visibility
  const handleLogsToggle = useCallback(() => {
    setLogsExpanded(prev => !prev);
  }, []);

  // Handler for copying debug info to clipboard
  const handleCopyDebugInfo = useCallback(async () => {
    try {
      await copyBacktestDebugInfo(backtest);
      setDebugCopySuccess(true);
      // Reset the checkmark after 2 seconds
      setTimeout(() => setDebugCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy debug info:', err);
    }
  }, [backtest]);

  // Determine if backtest is starting (no progress yet)
  const isStarting = !backtest?.progress;

  // Get status for data attribute
  const statusCode = normalizeBacktestResultStatus(backtest) || (backtest?.stage || '').toLowerCase();

  // Get version from backtest (normalized data has it at top level)
  const version = backtest?.version ?? backtest?.revision?.version ?? null;

  return (
    <div
      className={`backtest-status-card ${className}`}
      data-testid="backtest-status-card"
    >
      <div
        className="backtest-status-group"
        data-status={statusCode}
      >
        <div className="status-content">
          {/* BacktestProgress component */}
          <BacktestProgress
            progress={backtest?.progress}
            chartData={chartData}
            isStarting={isStarting}
            stage={backtest?.stage}
            status={statusCode || backtest?.status}
            elapsedTime={elapsedTime}
            showBenchmarkToggle={false}
            startDate={propStartDate || backtest?.start_date || backtest?.startDate || backtest?.backtestStartDate}
            endDate={propEndDate || backtest?.end_date || backtest?.endDate || backtest?.backtestEndDate}
            version={version}
          />

          {/* Stop Button */}
          <div className="stop-button-container">
            <Button
              variant="danger"
              onClick={handleStop}
              className={`stop-backtest-button ${isStopping ? 'stopping' : ''}`}
              disabled={isStopping}
            >
              {isStopping ? (
                <>
                  <Spinner animation="border" size="sm" className="stop-spinner" />
                  <span>Stopping...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faTimes} className="stop-icon" />
                  <span>Stop Backtest</span>
                </>
              )}
            </Button>
          </div>

          {/* Live Logs Section */}
          {showLogs && backtest?.id && (
            <div className="backtest-card-live-logs-section">
              <div className="backtest-card-live-logs-controls">
                <button
                  onClick={handleLogsToggle}
                  className="backtest-card-live-logs-toggle-button"
                >
                  <FontAwesomeIcon
                    icon={logsExpanded ? faChevronUp : faChevronDown}
                    className="me-2"
                  />
                  {logsExpanded ? 'Hide Live Logs' : 'Show Live Logs'}
                </button>
              </div>

              {logsExpanded && (
                <div className="backtest-card-live-logs-wrapper">
                  <BacktestLogs
                    backtestId={backtest.id}
                    getApiToken={getApiToken}
                    expanded={true}
                    hideToggleButton={true}
                    onToggle={() => {}}
                  />
                </div>
              )}
            </div>
          )}

          {/* Debug Copy Button - bottom right corner */}
          <div className="bsc-bottom-actions">
            <button
              type="button"
              className={`bsc-debug-copy-btn ${debugCopySuccess ? 'success' : ''}`}
              onClick={handleCopyDebugInfo}
              title="Copy backtest debug info for AI tools"
            >
              <FontAwesomeIcon icon={debugCopySuccess ? faCheck : faCopy} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
